import React from "react";

/**
 * Per-component crash isolation for the SERVER render, where error boundaries
 * do not run: a wrapped component that throws renders null and logs, so bad
 * CMS data costs one component rather than a 500. Covers its OWN body only.
 *
 * Only EXPORTED components are wrapped. A module-private one rendered as an
 * element (`<Card/>`) runs after the parent's try has returned, so a server
 * throw there is not isolated — wrap any private one that reads CMS data.
 *
 * In the browser the component is returned unwrapped: calling it inline would
 * put its hooks on the wrapper. A browser throw goes to whatever error boundary
 * the host app put above it, if any — the app documents where it has one.
 */

const IS_SERVER = typeof window === "undefined";

const FORWARD_REF = Symbol.for("react.forward_ref");
const MEMO = Symbol.for("react.memo");
const POSTPONE = Symbol.for("react.postpone");

type Renderable = { then?: unknown };

/* Thrown on purpose to steer the render; swallowing any breaks routing or
   streaming. Rethrows thenables, React's postpone, and ANY object with a string
   `digest` — Next's redirect / notFound / dynamic-usage / CSR bail-out, but also
   an error React has already serialised. Two match by MESSAGE TEXT: React's
   `use()` "Suspense Exception" (react-dom-server) and Next's "needs to bail out of
   prerendering" (app-render/dynamic-rendering.js) — checked on Next 15.5.18 and
   its bundled React; re-check on an upgrade. */
export function isControlFlowThrow(error: unknown, seen: Set<unknown> = new Set()): boolean {
  if (typeof error !== "object" || error === null || seen.has(error)) {
    return false;
  }
  seen.add(error);

  const thrown = error as { then?: unknown; digest?: unknown; message?: unknown; $$typeof?: unknown; cause?: unknown };
  if (typeof thrown.then === "function" || typeof thrown.digest === "string" || thrown.$$typeof === POSTPONE) {
    return true;
  }

  const message = typeof thrown.message === "string" ? thrown.message : "";
  if (message.startsWith("Suspense Exception") || message.includes("needs to bail out of prerendering")) {
    return true;
  }

  return "cause" in thrown && isControlFlowThrow(thrown.cause, seen);
}

function recover(name: string, error: unknown): null {
  if (isControlFlowThrow(error)) {
    throw error;
  }

  console.error(`[DDS] ${name} failed`, error);
  return null;
}

function renderSafely(name: string, render: () => unknown): any {
  try {
    const result = render() as Renderable;
    // An async Server Component rejects instead of throwing.
    if (result && typeof result.then === "function") {
      return (result as Promise<unknown>).then(undefined, error => recover(name, error));
    }
    return result;
  } catch (error) {
    return recover(name, error);
  }
}

/**
 * On the server only, wraps a function, `forwardRef` or `memo` component so a
 * render throw renders null; statics are copied for function and `forwardRef`
 * components, not for `memo`. In the browser the component is returned as-is.
 */
export function withSafeRender<
  T extends((...args: any[]) => unknown) | React.ForwardRefExoticComponent<any> | React.MemoExoticComponent<any>
>(component: T, name: string): T {
  const candidate = component as any;

  if (!IS_SERVER) {
    // Keeps DevTools on the public name rather than `XImpl`.
    if (typeof candidate === "function" || candidate?.$$typeof === FORWARD_REF || candidate?.$$typeof === MEMO) {
      candidate.displayName = name;
    }
    return component;
  }

  if (typeof candidate === "function") {
    if (candidate.prototype?.isReactComponent) {
      return component;
    }

    const Safe = (...args: unknown[]) => renderSafely(name, () => candidate(...args));
    Object.assign(Safe, candidate);
    Safe.displayName = name;
    return Safe as T;
  }

  if (candidate?.$$typeof === FORWARD_REF) {
    const Safe: any = React.forwardRef((props, ref) => renderSafely(name, () => candidate.render(props, ref)));
    Object.assign(Safe, candidate, {
      $$typeof: FORWARD_REF,
      render: Safe.render
    });
    Safe.displayName = name;
    return Safe as T;
  }

  if (candidate?.$$typeof === MEMO) {
    const Safe: any = React.memo(withSafeRender(candidate.type, name), candidate.compare);
    Safe.displayName = name;
    return Safe as T;
  }

  return component;
}
